import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * Single source of truth for the appointment `sponsorType` form-enum ↔ wire
 * mapping. The form uses **"Self-Pay"** (capital P, for tab display); the
 * backend wire literal is **"Self-pay"** (lowercase p, Flutter constant
 * `paymentTypeSelfPay`). Insurance / Corporate are identical on both sides.
 *
 * This casing was duplicated inline in three boundaries (transform-form-to-
 * request, form-edit-overlay, appointment-to-form-values); a mismatch in any
 * one silently corrupts the wire value on save (Samer review PR #457 §4).
 * Centralised here + round-trip unit-tested.
 */
type FormSponsorType = AppointmentFormValues['sponsorType'];

/** Form enum → wire literal. "Self-Pay" → "Self-pay"; others pass through. */
export function sponsorTypeToWire(form: FormSponsorType | null | undefined): string {
  return form === 'Self-Pay' ? 'Self-pay' : (form ?? 'Self-pay');
}

/**
 * Wire literal → form enum. "Self-pay" (any casing) → "Self-Pay"; Insurance /
 * Corporate pass through. Unrecognised values warn + default to Self-Pay so a
 * new backend sponsor type surfaces in dev/QA instead of being masked
 * (Samer review PR #408 §1).
 */
export function sponsorTypeFromWire(
  wire: string | null | undefined,
): FormSponsorType {
  if (!wire) return 'Self-Pay';
  if (wire.toLowerCase() === 'self-pay') return 'Self-Pay';
  if (wire === 'Insurance') return 'Insurance';
  if (wire === 'Corporate') return 'Corporate';
  // eslint-disable-next-line no-console
  console.warn(
    `[sponsor-type-wire] Unrecognised sponsorType "${wire}" — defaulting to Self-Pay.`,
  );
  return 'Self-Pay';
}
